import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Toast from '../components/Toast/Toast';
import PlayerPreview from '../components/PlayerPreview/PlayerPreview';
import Modal from '../components/WidgetCustomization/components/Modal/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { generateEmbedCode } from '../utils/embedGenerator';

const Container = styled.div`
  height: 100vh;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  background-color: #fff;
  overflow-y: auto;
  border-radius: 7px;
  box-sizing: border-box;
`;

const PreviewHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: auto;
  min-height: 550px;
  max-height: calc(100vh - 200px);
  border-radius: 7px;
  background-color: ${props => props.background === 'light' ? '#f2f2f2' : '#1e1e1e'};
  padding: 2rem;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: auto;
  width: ${props => props.device === 'mobile' ? '375px' : '100%'};
  margin: ${props => props.device === 'mobile' ? '0 auto' : '0'};

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #666;
  }
`;

const PreviewTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 500;
`;

const BackgroundToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #f5f5f5;
  padding: 0.25rem;
  border-radius: 4px;
`;

const ToggleButton = styled.button`
  padding: 0.25rem 1rem;
  background: ${props => props.active ? '#333' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#333'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
`;

const EmbedSection = styled.div`
  width: 100%;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 7px;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 300px;
`;

const EmbedTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const CodeBlock = styled.pre`
  background-color: #ffffff;
  color: #000000;
  padding: 1rem;
  padding-top: 3rem;
  border-radius: 4px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
  border: 1px solid #e0e0e0;
  max-height: 45vh;
  overflow: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
    
    &:hover {
      background: #666;
    }
  }

  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;

  code {
    display: block;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

const CopyButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  z-index: 1;

  &:hover {
    background-color: #444;
  }
`;

const VerticalPlayer = styled.div`
  width: 300px;
  background: #000000;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: white;
`;

const AlbumArtwork = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: #333;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  text-align: center;
`;

const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SongTitle = styled.div`
  font-size: 0.9rem;
  color: #fff;
`;

const ArtistName = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.8;
  }
`;

const PlayerBuilder = ({ widgetState }) => {
    const [background, setBackground] = useState('light');
    const [showToast, setShowToast] = useState(false);
    const [embedCode, setEmbedCode] = useState('');
    const [helpModal, setHelpModal] = useState({ show: false, title: '', content: '' });

    useEffect(() => {
        if (widgetState) {
            setEmbedCode(generateEmbedCode(widgetState));
        }
    }, [widgetState]);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(embedCode).then(() => {
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
        });
    };

    const showHelp = (title, content) => {
        setHelpModal({
            show: true,
            title,
            content: <div dangerouslySetInnerHTML={{ __html: content }} />
        });
    };

    return (
        <Container>
            <PreviewHeader>
                <PreviewTitle>Preview</PreviewTitle>
                <BackgroundToggle>
                    <ToggleButton
                        active={background === 'light'}
                        onClick={() => setBackground('light')}
                    >
                        Light
                    </ToggleButton>
                    <ToggleButton
                        active={background === 'dark'}
                        onClick={() => setBackground('dark')}
                    >
                        Dark
                    </ToggleButton>
                </BackgroundToggle>
            </PreviewHeader>

            <PreviewSection background={background} device={widgetState?.device}>
                <PlayerPreview background={background} widgetState={widgetState} />
            </PreviewSection>

            <EmbedSection>
                <EmbedTitle>
                    Embed Code
                    <FontAwesomeIcon
                        icon={faQuestionCircle}
                        onClick={() => showHelp(
                            'Embed Code',
                            'Copy the embed code from the builder and paste it into your HTML file where you want the player to appear. Make sure you have an internet connection to load the required resources. For detailed instructions, check out our <a href="/docs/implementation-guide" target="_blank">Implementation Guide</a>.'
                        )}
                        style={{ cursor: 'pointer', marginLeft: '0.5rem' }}
                    />
                </EmbedTitle>
                <CopyButton onClick={copyToClipboard}>Copy Code</CopyButton>
                <CodeBlock>
                    <code>{embedCode}</code>
                </CodeBlock>
            </EmbedSection>

            <Toast
                message="Copied to clipboard!"
                show={showToast}
                onHide={() => setShowToast(false)}
            />

            {helpModal.show && (
                <Modal
                    title={helpModal.title}
                    content={helpModal.content}
                    onClose={() => setHelpModal({ show: false, title: '', content: '' })}
                />
            )}
        </Container>
    );
};

export default PlayerBuilder; 